import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, Router } from '@angular/router';
import { HttpClient, HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { throwError } from 'rxjs';
import { TenantContextService } from '../auth/tenant-context.service';
import { environment } from '../../environments/environment';

export const REPORT_CURRENCY_QUERY = 'reportCurrency';
export const BOUND_REPORT_CURRENCY = new HttpContextToken<string | null>(() => null);
@Injectable()
export class BoundReportCurrency { currency: string | null = null; }
export function createBoundReportClient(http: HttpClient, scope: BoundReportCurrency): HttpClient {
  return new HttpClient({handle: request => http.request(scope.currency === null ? request : request.clone({context: request.context.set(BOUND_REPORT_CURRENCY, scope.currency)}))});
}
export function isReportCurrency(value: unknown): value is string {
  return typeof value === 'string' && (value === 'UNKNOWN' || /^[A-Z0-9]{1,10}$/.test(value));
}

@Injectable({ providedIn: 'root' })
export class ReportCurrencyContext {
  private readonly storageKey = 'report-currency:' + inject(TenantContextService).getTenantId();
  currency: string | null = null;

  constructor() {
    try {
      const saved = sessionStorage.getItem(this.storageKey);
      if (isReportCurrency(saved)) this.currency = saved;
    } catch { /* Selection still works when browser storage is unavailable. */ }
  }

  select(currency: string): void {
    if (!isReportCurrency(currency)) return;
    this.currency = currency;
    try { sessionStorage.setItem(this.storageKey, currency); } catch { /* Keep in-memory selection. */ }
  }
}

export function requiresReportCurrency(url: string, baseUrl: string): boolean {
  const base = new URL(baseUrl, window.location.origin);
  const target = new URL(url, window.location.origin);
  const prefix = base.pathname.replace(/\/$/, '');
  if (target.origin !== base.origin || !target.pathname.startsWith(prefix + '/')) return false;
  const path = target.pathname.slice(prefix.length).replace(/\/$/, '');
  if (path === '/analytics/channels/devices') return false;
  return /^\/(analytics|metrics|query|ai|reports)(\/|$)/.test(path)
    || path === '/ask' || path === '/crm/players/search' || path === '/crm/risk/recompute'
    || /^\/(crm\/)?players\/[^/]+\/(360|casino|timeline|tickets|wallet-transactions|bonuses|stats)$/.test(path);
}

export const ReportCurrencyInterceptor: HttpInterceptorFn = (request, next) => {
  if (!requiresReportCurrency(request.url, environment.crmServiceUrl)) return next(request);
  const boundCurrency = request.context.get(BOUND_REPORT_CURRENCY);
  if (boundCurrency !== null) {
    if (!isReportCurrency(boundCurrency)) return throwError(() => new HttpErrorResponse({status: 400, error: {code: 'CURRENCY_REQUIRED'}}));
    return next(request.clone({params: request.params.set('currency', boundCurrency)}));
  }
  const context = inject(ReportCurrencyContext);
  // Navigation targets take precedence so browser back/forward never reuses the
  // previous page currency while new report components are being constructed.
  const requested = inject(Router).getCurrentNavigation()?.finalUrl?.queryParams[REPORT_CURRENCY_QUERY];
  if (isReportCurrency(requested)) context.select(requested);
  const currency = context.currency;
  if (!currency) {
    return throwError(() => new HttpErrorResponse({ status: 400, statusText: 'Report currency required', error: { code: 'CURRENCY_REQUIRED' } }));
  }
  return next(request.clone({ params: request.params.set('currency', currency) }));
};

// A currency change recreates report components, cancelling their old view state
// and running their ordinary loaders under the selected currency.
@Injectable()
export class ReportCurrencyRouteReuseStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
    const reports = future.pathFromRoot.some(route => ['analytics', 'crm'].includes(route.routeConfig?.path || ''));
    if (reports && future.queryParamMap.get(REPORT_CURRENCY_QUERY) !== current.queryParamMap.get(REPORT_CURRENCY_QUERY)) return false;
    return super.shouldReuseRoute(future, current);
  }
}
